import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { useTranslation } from "react-i18next";
import {
  primaryColor,
  secondaryColor,
  tooltipContentStyle,
  tooltipCursorStyle,
} from "utils/dashboardUtils";
import {
  formatDateDDMMYYYY,
  formatLabel,
  formatLocalDate,
  formatUTCDate,
  isDesktop,
} from "utils/utils";
import CustomerLeadsFilter from "../CustomerLeadsFilter";

const safeFormatDate = (label) => {
  if (!label) return "";
  const formatted = formatDateDDMMYYYY(label);
  return formatted ?? label; // fallback to raw label if undefined
};

const WEEKDAY_AR = {
  Sun: "الأحد",
  Mon: "الاثنين",
  Tue: "الثلاثاء",
  Wed: "الأربعاء",
  Thu: "الخميس",
  Fri: "الجمعة",
  Sat: "السبت",
};

// Short forms keep the rotated label compact on mobile so it doesn't
// collide with the bar/date above it, mirroring the "Sun"/"Mon" length.
const WEEKDAY_AR_SHORT = {
  Sun: "أحد",
  Mon: "اثنين",
  Tue: "ثلاثاء",
  Wed: "أربعاء",
  Thu: "خميس",
  Fri: "جمعة",
  Sat: "سبت",
};

const localizeWeekday = (label, isRTL) => {
  if (!isRTL) return label;
  const map = isDesktop ? WEEKDAY_AR : WEEKDAY_AR_SHORT;
  return map[label] ?? label;
};

const CustomizedAxisTick = ({
  x,
  y,
  payload,
  index,
  data,
  shouldRotate,
  activeFilterType,
  isRTL,
}) => {
  const mainLabel = payload.value ?? "";
  const subLabel = data?.[index]?.subLabel ?? "";
  const rotateAngle = isRTL ? 35 : -35;
  const rotatedAnchor = isRTL ? "start" : "end";
  const subLabelFontSize = isRTL && !isDesktop ? 10 : 12;
  const mainLabelFontSize = isRTL && !isDesktop ? 10 : 12;

  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={14}
        textAnchor={shouldRotate ? rotatedAnchor : "middle"}
        style={{ direction: "ltr" }}
        fill={secondaryColor}
        fontSize={mainLabelFontSize}
        fontWeight={600}
        transform={shouldRotate ? `rotate(${rotateAngle})` : ""}
      >
        {activeFilterType === "DAY" || activeFilterType === ""
          ? formatLabel(
              mainLabel,
              activeFilterType,
              true,
              isRTL ? "ar" : "en",
            )
          : safeFormatDate(mainLabel)}
      </text>

      {subLabel && activeFilterType !== "MONTH" && (
        <text
          x={0}
          y={0}
          dx={isRTL ? 20 : -20}
          dy={shouldRotate ? 28 : 30}
          textAnchor={shouldRotate ? rotatedAnchor : "middle"}
          style={{ direction: "ltr" }}
          fill={secondaryColor}
          fontSize={subLabelFontSize}
          fontWeight={400}
          transform={shouldRotate ? `rotate(${rotateAngle})` : ""}
        >
          {localizeWeekday(subLabel, isRTL)}
        </text>
      )}
    </g>
  );
};

const LeadsGrowthChart = ({
  membershipGrowth,
  activeFilterType,
  className = "",
  showLeadsPageFilter = false,
  dates,
  setDates,
  setActiveFilter,
}) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";

  const MEMBERSHIP_GROWTH_DATA = membershipGrowth?.map((item) => ({
    month: item.label,
    subLabel: item.subLabel,
    newLeads: item.newLeads,
  }));

  // ── NEW: sum all newLeads across the full dataset ──
  const totalLeads =
    MEMBERSHIP_GROWTH_DATA?.reduce(
      (sum, item) => sum + (item.newLeads ?? 0),
      0,
    ) ?? 0;

  const getSubtitle = () => {
    switch (activeFilterType) {
      case "DAY":
        return t("Total new leads on ");
      case "WEEK":
        return t("Weekly leads overview");
      case "MONTH":
        return t("Monthly leads overview for current year");
      default:
        return t("Total new leads on");
    }
  };

  const shouldRotate = !isDesktop || MEMBERSHIP_GROWTH_DATA?.length > 6;

  // ── NEW: formatter for Y-axis ticks (e.g. 1000 → "1k") ──
  const formatYTick = (value) => {
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(1)}k`;
    return value;
  };

  const calculateInterVal = () => {
    if (isDesktop) {
      return 0;
    } else if (!isDesktop && activeFilterType === "MONTH") {
      return 4;
    }
  };

  return (
    <div className={`membership_growth dashboard_common_card ${className}`}>
      {/* Title row */}
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap membership_stat">
        <div className="lead_chart_heading">
          <div className="d-flex gap-2">
            <h3 className="fs_20">{t("Leads Growth")}</h3>
          </div>
          <div className="d-flex gap-1">
            <p className="page_sub_title mt-2">{getSubtitle()}</p>
            {(activeFilterType === "DAY" || activeFilterType === "") &&
              dates?.[0] && (
                <p className="page_sub_title mt-2">
                  {formatLocalDate(dates[0])} {/* ← no toISOString() */}
                </p>
              )}
          </div>
        </div>

        <CustomerLeadsFilter
          dates={dates}
          setDates={setDates}
          setActiveFilter={setActiveFilter}
          className={"leads_stats mb-0"}
          selectDateLabel={false}
          selectTypeLabel={false}
        />

        {/* Legend */}
        <div className="legends d-flex flex-column gap-1">
          <div className="leads_count d-flex gap-1">
            <p>{t("Total Leads :")}</p> <p>{totalLeads.toLocaleString()}</p>
          </div>
          <div className="inner_legend">
            <span className="dot new_dot new_lead_dot" />
            {t("New Leads")}
          </div>
        </div>
      </div>

      {/* Chart */}
      <ResponsiveContainer
        width="100%"
        height={300}
        className={`leads_growth_chart`}
      >
        <BarChart
          data={MEMBERSHIP_GROWTH_DATA}
          margin={{ top: 20, right: isDesktop ? 30 : 10 , left: 10, bottom: isDesktop ? 20 : 0  }}
          barCategoryGap="30%"
          barGap={4}
        >
          <XAxis
            dataKey="month"
            axisLine={false}
            tickLine={false}
            interval={calculateInterVal}
            height={shouldRotate ? 60 : 50}
            tick={(props) => (
              <CustomizedAxisTick
                {...props}
                data={MEMBERSHIP_GROWTH_DATA}
                shouldRotate={shouldRotate}
                activeFilterType={activeFilterType}
                isRTL={isRTL}
              />
            )}
            tickMargin={shouldRotate ? 0 : 15}
            tickFormatter={shouldRotate ? undefined : (label) => label}
          />

          {/* ── NEW: visible Y-axis with lead counts ── */}
          <YAxis
            axisLine={false}
            tickLine={false}
            tickFormatter={formatYTick}
            tick={{ fill: secondaryColor, fontSize: 12, fontWeight: 500 }}
            width={30}
            allowDecimals={false}
          />

          <Tooltip
            cursor={tooltipCursorStyle}
            formatter={(value, name) => [value, t("New Leads")]}
            labelFormatter={(label) =>
              activeFilterType === "DAY" || activeFilterType === ""
                ? formatLabel(label, activeFilterType, true, isRTL ? "ar" : "en")
                : safeFormatDate(label)
            }
            contentStyle={tooltipContentStyle}
            labelStyle={{ fontWeight: 600 }}
          />

          <CartesianGrid
            vertical={false}
            stroke={secondaryColor}
            strokeDasharray="1"
            opacity={0.6}
          />

          <Bar dataKey="newLeads" fill="#38BDF8" radius={[0, 0, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default LeadsGrowthChart;
